import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';

interface DuckPostWidgetOptions {
  apiBaseUrl: string;
  organizationId: string;
  mode: 'button';
  type: 'modal';
  badgeStyle: string;
  target: HTMLElement;
  theme: 'light';
  pollIntervalMs: number;
}

export interface DuckPostWidgetInstance {
  closeUpdates(): void;
  destroy(): void;
}

export interface DuckPostWidgetApi {
  init(options: DuckPostWidgetOptions): DuckPostWidgetInstance;
}

type WidgetWindow = Window & { DuckPostWidget?: DuckPostWidgetApi };

@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  readonly publicPageUrl = `https://duckpost.app/updates/${environment.duckpostOrganizationSlug}`;

  private loading?: Promise<DuckPostWidgetApi>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone
  ) {}

  attach(target: HTMLElement): () => void {
    let detached = false;
    let instance: DuckPostWidgetInstance | undefined;
    let unbindKeyboard: (() => void) | undefined;

    this.zone.runOutsideAngular(() => {
      this.loadWidget()
        .then(widget => {
          if (!detached && target.isConnected) {
            instance = widget.init({
              apiBaseUrl: 'https://duckpost.app',
              // Public organization identifier from DuckPost's install snippet; production has its own organization.
              organizationId: environment.duckpostOrganizationId,
              mode: 'button',
              type: 'modal',
              badgeStyle: 'top: -5px; right: -3px; transform: scale(0.75); transform-origin: top right;',
              target,
              theme: 'light',
              pollIntervalMs: 60_000,
            });
            unbindKeyboard = this.bindKeyboard(instance, target);
          }
        })
        .catch(() => {
          // The bell remains a normal link to the hosted changelog if loading fails.
        });
    });

    return () => {
      if (!detached) {
        detached = true;
        unbindKeyboard?.();
        instance?.destroy();
      }
    };
  }

  // DuckPost's modal has no Escape handling or focus management: close it on Escape,
  // move focus into it when it opens and back to the bell when it closes.
  private bindKeyboard(instance: DuckPostWidgetInstance, target: HTMLElement): (() => void) | undefined {
    const panel = this.document.querySelector<HTMLElement>('[data-duckpost-widget-panel]');
    if (!panel) {
      return undefined;
    }

    const isOpen = () => panel.style.display !== 'none';
    let wasOpen = isOpen();
    const observer = new MutationObserver(() => {
      const open = isOpen();
      if (open === wasOpen) {
        return;
      }
      wasOpen = open;
      const active = this.document.activeElement;
      if (open) {
        panel.shadowRoot?.querySelector<HTMLElement>('[data-duckpost-close]')?.focus({ preventScroll: true });
      } else if (!active || active === this.document.body || active === panel) {
        target.focus({ preventScroll: true });
      }
    });
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen()) {
        // The modal sits above every TelQ dialog, so it alone handles this Escape.
        event.stopPropagation();
        instance.closeUpdates();
      }
    };

    observer.observe(panel, { attributes: true, attributeFilter: ['style'] });
    this.document.addEventListener('keydown', onKeydown, true);
    return () => {
      observer.disconnect();
      this.document.removeEventListener('keydown', onKeydown, true);
    };
  }

  private loadWidget(): Promise<DuckPostWidgetApi> {
    const view = this.document.defaultView as WidgetWindow | null;
    if (view?.DuckPostWidget) {
      return Promise.resolve(view.DuckPostWidget);
    }
    if (this.loading) {
      return this.loading;
    }
    if (!view) {
      return Promise.reject(new Error('Release notes require a browser.'));
    }

    this.loading = new Promise<DuckPostWidgetApi>((resolve, reject) => {
      const script = this.document.createElement('script');
      const finish = (widget?: DuckPostWidgetApi) => {
        view.clearTimeout(timeout);
        script.onload = null;
        script.onerror = null;
        if (widget) {
          resolve(widget);
        } else {
          script.remove();
          reject(new Error('Could not load the release notes widget.'));
        }
      };
      const timeout = view.setTimeout(() => finish(), 15_000);
      script.async = true;
      script.src = 'https://widget.duckpost.app/widget/v1/widget.js?v=1.0.0';
      script.onload = () => finish(view.DuckPostWidget);
      script.onerror = () => finish();
      this.document.head.appendChild(script);
    }).catch(error => {
      this.loading = undefined;
      throw error;
    });

    return this.loading;
  }
}
